import {
  Button,
  Header,
  Layout,
  pxToRem,
  Stack,
  styled,
  Text,
} from '@lessonnine/design-system.lib';
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';
import get from 'lodash/fp/get';
import PropTypes from 'prop-types';
import React from 'react';
// External Imports
import { connect } from 'react-redux';

import { siteMaxWidth } from '../../config/css/ui.mjs';
import { options } from '../../config/reactRedux.mjs';
import {
  FOOTER,
  FOOTER_COLUMN_1,
  FOOTER_COLUMN_2,
  FOOTER_COLUMN_3,
  FOOTER_COPYRIGHT,
} from '../../config/types/menu.mjs';
import { CONTENT_ITEM_SINGLE } from '../../config/types/path.mjs';
import { LANGUAGES, MENU } from '../../config/types/prop.mjs';
import { useTrackPageCompleted } from '../../hooks/useTrackPageCompleted/index.mjs';
import { selectEdition } from '../../selectors/selectEdition.mjs';
import { selectIs404 } from '../../selectors/selectIs404.mjs';
import { selectMatch } from '../../selectors/selectMatch.mjs';
import { selectMenuTopLevel } from '../../selectors/selectMenu.mjs';
import { selectPageTrackingData } from '../../selectors/selectPageTrackingData.mjs';
import { pageType, trackPageEvent } from '../../utils/tracking.mjs';
// Internal Imports
import { buildCTALink, buildMagazineHomepageLink } from '../../utils/url.mjs';
import { EditionPicker } from '../EditionPicker/EditionPicker.jsx';
import { I18n } from '../I18n/I18n.jsx';
import { Separator } from '../Separator/Separator.jsx';
import { SocialLinks } from '../SocialLinks/SocialLinks.jsx';
import { FooterNav } from './FooterNav.jsx';

const FooterStack = styled(Stack)`
  border-top: 1px solid ${({ theme }) => theme.color.surface.layer.border};
`;

const LayoutWithMaxWidth = styled(Layout)`
  max-width: ${pxToRem(siteMaxWidth)};
  width: 100%;
`;

const AltLinkContainer = styled.div`
  display: inline-block;
  min-width: ${pxToRem(254)};
  position: relative;
`;

const FlexNav = styled.nav`
  flex: 1 1 auto;
`;

const FooterBottomNav = styled.nav`
  margin: 0 auto;
  width: 100%;
`;

const sectionsGap = 60;
const sectionsPadding = [0, 15];

// Component Definition
function Footer({
  columnOne,
  columnThree,
  columnTwo,
  copyright,
  currentEdition,
  currentLanguage,
  editions,
  is404,
  pageTrackingData,
}) {
  const { data: trackingData } = pageTrackingData;
  const footerElement = useTrackPageCompleted({
    is404,
    trackingData,
    trackingEnabled: trackingData?.page_type !== pageType[CONTENT_ITEM_SINGLE],
  });
  const goLink = buildCTALink(currentEdition);
  const ctaLabel = <I18n defaultText="Try Babbel" field="try-babbel" />;
  const ctaHeading = <I18n defaultText="Learn a new language" field="learn-a-new-language" />;
  const trackFooterEvent = () => {
    trackPageEvent({
      cta_placement: 'footer',
      link_url: goLink,
      name: 'magazine_page:cta_clicked',
      version: 2,
      ...trackingData,
    });
  };

  return (
    <footer ref={footerElement}>
      <FooterStack alignItems="center" gap={sectionsGap} padding={[70, 0]}>
        <LayoutWithMaxWidth direction="stack" gap={sectionsGap} padding={sectionsPadding}>
          <EditionPicker currentEdition={currentEdition} editions={editions} />
          <Stack gap={tokens.size.space.medium}>
            {/* eslint-disable-next-line react/jsx-pascal-case -- design-system's interface */}
            <Header.h2>
              <I18n defaultText="More Babbel" field="footer-title" />
            </Header.h2>
            <Layout align="leading" direction={{ small: 'inline', xsmall: 'stack' }} gap={40}>
              <FlexNav aria-label="About Babbel:">
                <FooterNav
                  currentLanguage={currentLanguage}
                  heading="About Babbel:"
                  headingTranslation="about-babbel"
                  items={columnOne}
                />
              </FlexNav>
              <FlexNav aria-label="Learn Languages Online:">
                <FooterNav
                  currentLanguage={currentLanguage}
                  heading="Learn Languages Online:"
                  headingTranslation="learn-languages-online"
                  items={columnTwo}
                />
              </FlexNav>
              <FlexNav aria-label="This page in other languages:">
                <FooterNav
                  currentLanguage={currentLanguage}
                  heading="This page in other languages:"
                  headingTranslation="this-page-in-other-languages"
                  items={columnThree}
                />
              </FlexNav>
              <Stack gap={15}>
                <Text fontSize="medium" fontWeight="600" letterSpacing="0.06">
                  {ctaHeading}
                </Text>
                <AltLinkContainer>
                  <Button
                    as="a"
                    fullWidth
                    href={goLink}
                    onClick={() => trackFooterEvent()}
                    size="jumbo"
                  >
                    {ctaLabel}
                  </Button>
                </AltLinkContainer>
              </Stack>
            </Layout>
          </Stack>
        </LayoutWithMaxWidth>
        <Separator />
        <LayoutWithMaxWidth
          alignItems="center"
          direction={{ medium: 'inline', small: 'stackReverse' }}
          gap={tokens.size.space.medium}
          padding={sectionsPadding}
        >
          {/* TODO: add translations for the aria label text */}
          <FooterBottomNav aria-label="Copyright">
            <FooterNav copyright currentLanguage={currentLanguage} items={copyright} />
          </FooterBottomNav>
          <SocialLinks />
        </LayoutWithMaxWidth>
      </FooterStack>
    </footer>
  );
}

Footer.propTypes = {
  columnOne: PropTypes.arrayOf(PropTypes.shape(MENU)).isRequired,
  columnThree: PropTypes.arrayOf(PropTypes.shape(MENU)).isRequired,
  columnTwo: PropTypes.arrayOf(PropTypes.shape(MENU)).isRequired,
  copyright: PropTypes.arrayOf(PropTypes.shape(MENU)).isRequired,
  currentEdition: LANGUAGES.isRequired,
  currentLanguage: LANGUAGES.isRequired,
  editions: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types -- dynamically built
  is404: PropTypes.bool.isRequired,
  pageTrackingData: PropTypes.shape({
    data: PropTypes.object, // eslint-disable-line react/forbid-prop-types -- dynamically built
    isLoading: PropTypes.bool,
  }).isRequired,
};

const mapStateToProps = (state) => {
  const { params } = selectMatch(state);
  const editionNames = get('languages.translations', state);
  const currentEdition = selectEdition(state);
  const pageTrackingData = selectPageTrackingData(state);
  const editions = {};

  for (const [languageCode, editionName] of Object.entries(editionNames || {})) {
    editions[languageCode] = {
      link: buildMagazineHomepageLink(languageCode),
      name: editionName,
    };
  }

  return {
    columnOne: selectMenuTopLevel(state, FOOTER_COLUMN_1),
    columnThree: selectMenuTopLevel(state, FOOTER_COLUMN_3),
    columnTwo: selectMenuTopLevel(state, FOOTER_COLUMN_2),
    copyright: selectMenuTopLevel(state, FOOTER_COPYRIGHT),
    currentEdition,
    currentLanguage: params.language,
    editions,
    footer: selectMenuTopLevel(state, FOOTER),
    is404: selectIs404(state),
    pageTrackingData,
  };
};

const withRedux = connect(mapStateToProps, undefined, undefined, options);
const FooterHoc = withRedux(Footer);

// Module Exports
export { FooterHoc as Footer };
